// Company logo with progressive fallback.
//
// Tries Logo.dev (vector-quality brand logos via a public token) and falls
// back to Google's favicon service. If both fail, renders a calm colored
// initial disc so the UI never shows a broken-image icon.

import { useState } from "react";

interface Props {
  domain?: string | null;
  co: string;
  size?: number;
  radius?: number;
}

// Public Logo.dev token. Override via `?LOGO_DEV_TOKEN=...` if you have one.
const TOKEN = "pk_X-1ZO13GSgeOoUrIuJ6GMQ";

function initial(co: string) {
  const s = (co || "?").trim();
  return s.charAt(0).toUpperCase();
}

// Stable color picked from a small calm palette by hashing the company name —
// keeps the same company looking the same across reloads.
const PALETTE = ["#2a3a52", "#4d6a3e", "#7a5a2a", "#3a4859", "#5e4a6e", "#8a3f3a", "#1c1a16"];
function colorFor(co: string) {
  let h = 0;
  for (const c of co) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

export function Logo({ domain, co, size = 28, radius = 6 }: Props) {
  const [step, setStep] = useState(0);

  const sources = domain
    ? [
        `https://img.logo.dev/${domain}?token=${TOKEN}&size=${size * 4}&format=png`,
        `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`,
      ]
    : [];

  const placeholder = (
    <span style={{
      width: size, height: size, borderRadius: radius,
      background: colorFor(co), color: "var(--bg)",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.max(10, size * 0.45), fontWeight: 600,
      letterSpacing: "-0.02em", flex: "none",
    }}>{initial(co)}</span>
  );

  if (step >= sources.length) return placeholder;

  return (
    <img
      src={sources[step]}
      alt=""
      width={size}
      height={size}
      style={{
        borderRadius: radius,
        objectFit: "contain",
        background: "var(--surface)",
        flex: "none",
      }}
      onError={() => setStep((s) => s + 1)}
    />
  );
}
